/**
 * Every CLIENT-SIDE media preference, in one place.
 *
 * ARCHITECTURAL RULE (docs/ARCHITECTURE.md §3): all media processing runs in
 * the browser. The server never transcodes — that is what keeps ~300 concurrent
 * participants on one EX44 — so noise suppression, segmentation and gain live
 * here and are published as already-processed tracks.
 */

/**
 * `native`   — the WebRTC `noiseSuppression` constraint (free, always there).
 * `rnnoise`  — the RNNoise WASM AudioWorklet in the publish path. Falls back to
 *              `native` automatically when the WASM cannot load.
 */
export type NoiseSuppressionMode = 'off' | 'native' | 'rnnoise';

export type BlurStrength = 'light' | 'strong';

export type VideoEffectKind = 'none' | 'blur' | 'image';

export interface AudioProcessingPreferences {
  readonly noiseSuppression: NoiseSuppressionMode;
  readonly echoCancellation: boolean;
  readonly autoGainControl: boolean;
}

export interface VideoEffectPreferences {
  readonly kind: VideoEffectKind;
  readonly blurStrength: BlurStrength;
  /** Id from `backgroundCatalog`, or a `custom:*` id from the upload store. */
  readonly backgroundId: string | null;
}

export interface DevicePreferences {
  readonly cameraId: string | null;
  readonly micId: string | null;
  readonly speakerId: string | null;
}

export interface MediaPreferences {
  readonly audio: AudioProcessingPreferences;
  readonly video: VideoEffectPreferences;
  readonly devices: DevicePreferences;
  /** User-pinned low-bandwidth mode. Independent of the automatic ladder. */
  readonly lowBandwidth: boolean;
}

export const DEFAULT_AUDIO_PREFERENCES: AudioProcessingPreferences = {
  noiseSuppression: 'rnnoise',
  echoCancellation: true,
  autoGainControl: true,
};

export const DEFAULT_VIDEO_PREFERENCES: VideoEffectPreferences = {
  kind: 'none',
  blurStrength: 'strong',
  backgroundId: null,
};

export const DEFAULT_DEVICE_PREFERENCES: DevicePreferences = {
  cameraId: null,
  micId: null,
  speakerId: null,
};

export const DEFAULT_MEDIA_PREFERENCES: MediaPreferences = {
  audio: DEFAULT_AUDIO_PREFERENCES,
  video: DEFAULT_VIDEO_PREFERENCES,
  devices: DEFAULT_DEVICE_PREFERENCES,
  lowBandwidth: false,
};

/** How the denoiser actually ended up running — reported back to the UI. */
export type DenoiseRuntime = 'off' | 'native' | 'rnnoise' | 'unavailable';

/** Live status of the segmentation pipeline, surfaced in the settings modal. */
export interface VideoEffectRuntime {
  readonly kind: VideoEffectKind;
  /** True once frames are actually being composited with a real mask. */
  readonly active: boolean;
  /** Set when the perf budget stepped the effect down (or off). */
  readonly degraded: boolean;
  /** Plain-language explanation for the user. Null when nothing is wrong. */
  readonly reason: string | null;
  /** Measured composite frame rate, rounded. 0 before the first second. */
  readonly fps: number;
}

export const IDLE_VIDEO_EFFECT_RUNTIME: VideoEffectRuntime = {
  kind: 'none',
  active: false,
  degraded: false,
  reason: null,
  fps: 0,
};

/** Everything the UI needs to tell the user what their media plane is doing. */
export interface MediaProcessingStatus {
  readonly denoise: DenoiseRuntime;
  readonly effect: VideoEffectRuntime;
}

export const IDLE_PROCESSING_STATUS: MediaProcessingStatus = {
  denoise: 'off',
  effect: IDLE_VIDEO_EFFECT_RUNTIME,
};
